import React, { useMemo } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { ApprovalMediaList } from '../components/ApprovalMediaList';
import { MediaItemData } from '@/core/MediaItem/MediaItem.model';
import { usePublishTrail } from '@/core/navigation/TrailDisplayContext';
import { useLocale } from '@/hooks/useLocale';

interface ApprovalOutletContext {
  pendingMedia: MediaItemData[];
  clientId: string;
  token?: string;
  mediaStage?: string;
  onRateMedia: (mediaId: number, rating?: number, comment?: string, skipRefetch?: boolean) => Promise<void>;
  onUpdateMediaLabel: (mediaId: number, label: string) => Promise<void>;
  onMediaStageChange: (stage?: string) => void;
  loadMoreMedia: () => Promise<void>;
  hasNext: boolean;
  totalCount: number;
  isLoading: boolean;
}

export const ApprovalMediaRoute: React.FC = () => {
  const location = useLocation();
  const { t } = useLocale();
  const {
    pendingMedia,
    clientId,
    token,
    mediaStage,
    onRateMedia,
    onUpdateMediaLabel,
    onMediaStageChange,
    loadMoreMedia,
    hasNext,
    totalCount,
  } = useOutletContext<ApprovalOutletContext>();

  const isReel = location.pathname.includes('/reel');

  // Breadcrumb reflects the grid <-> feed depth. The middle crumb (Foto/Reel)
  // points at the grid (reviewItem stripped), so tapping it leaves the feed.
  const isFeed = new URLSearchParams(location.search).has('reviewItem');
  const isPending = mediaStage === 'pending_approval';
  const contentTitle = isReel ? 'Reel' : 'Foto';
  // Pending is a review (rate/comment); everything else is a view-only gallery.
  const feedLabel = isPending ? 'Revisione' : 'Anteprima';
  // useLocale() returns a fresh `t` each render, so depend on the resolved
  // string (stable by value) to keep the memo from recomputing every render.
  const dashboardLabel = t('client.trail.dashboard');
  const crumbs = useMemo(() => {
    const dashboard = { to: `/client/${clientId}`, label: dashboardLabel };
    const gridParams = new URLSearchParams(location.search);
    gridParams.delete('reviewItem');
    const gridQuery = gridParams.toString();
    const gridUrl = gridQuery ? `${location.pathname}?${gridQuery}` : location.pathname;
    const gridCrumb = { to: gridUrl, label: contentTitle };
    if (!isFeed) return [dashboard, gridCrumb];
    return [dashboard, gridCrumb, { to: `${location.pathname}${location.search}`, label: feedLabel }];
  }, [clientId, dashboardLabel, location.pathname, location.search, isFeed, contentTitle, feedLabel]);
  usePublishTrail(crumbs);

  return (
    <ApprovalMediaList
      mediaItems={pendingMedia}
      clientId={clientId}
      token={token}
      currentStage={mediaStage}
      contentLabel={isReel ? 'reel' : 'foto'}
      onStageChange={onMediaStageChange}
      onRate={onRateMedia}
      onUpdateLabel={onUpdateMediaLabel}
      onLoadMore={loadMoreMedia}
      hasMore={hasNext}
      totalCount={totalCount}
    />
  );
};
